import { APP_INITIALIZER, ErrorHandler, inject, NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { DOCUMENT } from "@angular/common";
import { RouteReuseStrategy } from "@angular/router";
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { IonicModule, IonicRouteStrategy } from "@ionic/angular";
import { provideTranslateLoader, provideTranslateService, TranslateLoader } from "@ngx-translate/core";
import * as SentryAngular from "@sentry/angular";
import { ActionReducer, MetaReducer, StoreModule, Store } from "@ngrx/store";
import { RootState, effects, reducers } from "src/app/store";
import { EffectsModule } from "@ngrx/effects";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { StoreRouterConnectingModule } from "@ngrx/router-store";
import { localStorageSync } from "ngrx-store-localstorage";
import { AppRoutingModule } from "src/app/app-routing.module";
import { AppComponent } from "src/app/app.component";
import { DEFAULT_LANGUAGE } from "src/app/constants";
import { environment } from "src/environments/environment";
import { ApiModule, Configuration } from "src/app/services/api";
import { persistedKeys as appPersistedKeys } from "src/app/store/app/state";
import { persistedKeys as userPersistedKeys } from "src/app/store/user/state";
import { LanguageInterceptor } from "src/app/interceptors/language/language.interceptor";
import { AuthInterceptor } from "src/app/interceptors/auth/auth.interceptor";
import { VersionInterceptor } from "src/app/interceptors/version/version.interceptor";

import { initializeApp } from "./app.initializer.factory";
import { serverStateSync } from "./store/server-state-sync";
import { InfoComponentModule } from "./modals/info/info.module";
import { SessionIdInterceptor } from "./interceptors/session-id/session-id.interceptor";
import { AppInterceptor } from "./interceptors/app/app.interceptor";
import { UTMInterceptor } from "./interceptors/utm/utm.interceptor";
import { UTMInitService } from "./services/utm/utm-init.service";
import { CustomLoader } from "./services/i18n/custom-loader";
import { AppTranslateModule } from "./services/i18n/translate.module";
import { androidTransitionAnimation } from "./animations/android-transition.animation";
import { BalanceNamePipe } from "./pipes/balance-name/balance-name.pipe";

export function createTranslateLoader(): TranslateLoader {
  const http = inject(HttpClient);
  const doc = inject(DOCUMENT);
  const [, , , slug] = doc.location.href.split("/");
  return new CustomLoader(http, slug);
}

export const localStorageReduxSync = (reducer: ActionReducer<RootState>): ActionReducer<any> =>
  localStorageSync({
    keys: [{ app: appPersistedKeys }, { user: userPersistedKeys }],
    rehydrate: true,
    storageKeySerializer: key => `${environment.name}_${key}`,
  })(reducer);

const metaReducers: MetaReducer<any, any>[] = [serverStateSync, localStorageReduxSync];

export const setApiConfig = (): Configuration => new Configuration({ basePath: environment.apiUrl });

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    IonicModule.forRoot({
      mode: "ios",
      navAnimation: androidTransitionAnimation,
    }),
    AppRoutingModule,
    StoreModule.forRoot(reducers, { metaReducers }),
    EffectsModule.forRoot(effects),
    StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: environment.production }),
    StoreRouterConnectingModule.forRoot(),
    AppTranslateModule,
    ApiModule.forRoot(setApiConfig),
    InfoComponentModule,
  ],
  providers: [
    // Please keep the order of the interceptors as they are
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: HTTP_INTERCEPTORS, useClass: AppInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true },
    { provide: ErrorHandler, useValue: SentryAngular.createErrorHandler() },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: VersionInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SessionIdInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: UTMInterceptor, multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: (utmService: UTMInitService) => (): void => utmService.init(),
      deps: [UTMInitService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [Store],
      multi: true,
    },
    ...provideTranslateService({
      fallbackLang: DEFAULT_LANGUAGE,
      loader: provideTranslateLoader(createTranslateLoader),
    }),
    provideHttpClient(withInterceptorsFromDi()),
    BalanceNamePipe,
  ],
})
export class AppModule {}
